import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type {
  ApprovalDecision,
  ApprovalViewer,
  ApproverKey,
  DecisionResponse,
  PriorityRating,
  RoleApprovalsResponse,
  SubmissionDecisionResponse,
  TransferOutcome,
} from '../types/roleApprovals';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

/** Pull the proxy's own message out of an error body — these routes explain refusals ("only that
 * approver can record this"), and replacing that with "Request failed (403)" loses the reason. */
async function errorFrom(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.error || body?.message || '';
  } catch {
    return '';
  }
}

/** The viewer as an older proxy revision sends it: one seat, and a flag for "as a delegate". */
type LegacyViewerFields = { approverKey?: ApproverKey | null; isDelegate?: boolean };

/**
 * The viewer with `approverKeys` / `delegateKeys` always present. The frontend and the proxy deploy
 * in parallel (`deploy.yml`), so this page can meet a proxy that still sends the single
 * `approverKey` / `isDelegate`; the cards read the arrays and would throw on a missing one.
 */
export function normaliseViewer(raw: Partial<ApprovalViewer> & LegacyViewerFields): ApprovalViewer {
  const legacyKeys = raw.approverKey ? [raw.approverKey] : [];
  const approverKeys = Array.isArray(raw.approverKeys) ? raw.approverKeys : legacyKeys;
  const delegateKeys = Array.isArray(raw.delegateKeys) ? raw.delegateKeys : raw.isDelegate ? legacyKeys : [];
  return {
    email: raw.email ?? '',
    isAdmin: Boolean(raw.isAdmin),
    canSetPriority: raw.canSetPriority,
    approverKeys,
    delegateKeys,
  };
}

/** The approval queue, change requests and the caller's own seats, from the proxy. */
export async function fetchRoleApprovals(): Promise<RoleApprovalsResponse> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/role-approvals`);
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
  const body = (await res.json()) as RoleApprovalsResponse;
  return { ...body, viewer: normaliseViewer(body.viewer ?? {}) };
}

/**
 * Record, change or withdraw one approver's decision.
 *
 * `decision: null` withdraws. That is a first-class action rather than an undo: because the proxy
 * DERIVES the proposal's status from the three cells, clearing one is what takes a declined role
 * back to `Proposed` — there is no separate "un-decline" to call.
 */
export async function recordApprovalDecision(
  proposalId: string,
  approverKey: ApproverKey,
  decision: ApprovalDecision,
  reason?: string,
  /** Only read by the proxy on the CEO's own approval — see `setProposalPriority`. */
  priorityRating?: PriorityRating | null
): Promise<DecisionResponse> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/role-approvals/${encodeURIComponent(proposalId)}/decision`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approverKey, decision, reason: reason || '', priorityRating: priorityRating ?? null }),
    }
  );
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
  return res.json() as Promise<DecisionResponse>;
}

/**
 * One approver's decision on every role of a bulk submission still waiting on them. Roles they have
 * already decided are left as they are — a changed mind is recorded on that role.
 */
export async function recordSubmissionDecision(
  submissionId: string,
  approverKey: ApproverKey,
  decision: Exclude<ApprovalDecision, null>,
  reason?: string,
  /** Per role id — read by the proxy on the CEO's own approval only, like `priorityRating`. */
  priorityRatings?: Record<string, PriorityRating | null>
): Promise<SubmissionDecisionResponse> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/role-approvals/submissions/${encodeURIComponent(submissionId)}/decision`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approverKey, decision, reason: reason || '', priorityRatings: priorityRatings ?? {} }),
    }
  );
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
  return res.json() as Promise<SubmissionDecisionResponse>;
}

/**
 * The CEO sets, changes or clears (`null`) a proposal's approved priority on its own. His APPROVAL
 * already carries the rating on screen, so this is only for changing it without approving, or
 * after. A change is posted in the request's thread in #squad_personnel_expense_planning.
 */
export async function setProposalPriority(
  proposalId: string,
  rating: PriorityRating | null
): Promise<{ approved: PriorityRating | null; notice: { notified: boolean; reason?: string } | null }> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/role-approvals/${encodeURIComponent(proposalId)}/priority`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating }),
    }
  );
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
  return res.json();
}

/**
 * Finish a hiring-plan copy that failed after the role was already approved.
 *
 * Safe to call repeatedly — the proxy's claim decides, so a role that did make it across creates
 * nothing on a second call. Offered instead of "withdraw and approve again", which would rewrite
 * the audit trail to make an Airtable blip look like an approver changing their mind.
 *
 * ⚠️ The RESULT has to be read, not discarded. This route answers **200 with
 * `transferred: false`** whenever the claim is still held — which is precisely the state after a
 * failed stamp: the role IS in the hiring plan, the proposal does not know it, and the two are
 * being counted twice. Reporting that as "done" tells someone the double-count is fixed while the
 * amber box is still on their screen.
 */
export async function retryHiringPlanTransfer(proposalId: string): Promise<TransferOutcome> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/role-approvals/${encodeURIComponent(proposalId)}/transfer`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
  const body = (await res.json()) as {
    transfer?: { transferred?: boolean; reason?: string; hiringPlanRecordId?: string; priorityDropped?: boolean };
  };
  const t = body?.transfer;
  if (t?.transferred) {
    return { ok: true, hiringPlanRecordId: t.hiringPlanRecordId, priorityDropped: Boolean(t.priorityDropped) };
  }

  // Nothing was created, and the reason decides whether a person is needed.
  if (t?.reason === 'already_claimed') {
    throw new Error(
      'A copy of this role was already created in the hiring plan, but the proposal was never ' +
        'stamped with its id — so the role is being counted twice. This needs fixing by hand: find ' +
        'the role in the hiring database and put its record id into the proposal\'s ' +
        'hiring_plan_record_id field. Retrying here would create a second copy.'
    );
  }
  if (t?.reason === 'already_transferred') return { ok: true, skipped: t.reason };
  throw new Error('The role was not copied into the hiring plan, and no reason was given.');
}

/** One decision on a change request to a hiring-plan role. The third approval applies it. */
export async function recordChangeDecision(
  requestId: string,
  approverKey: ApproverKey,
  decision: ApprovalDecision,
  reason?: string
): Promise<{ state: string; apply: { applied: boolean; state: string; message?: string } | null }> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/role-approvals/change-requests/${encodeURIComponent(requestId)}/decision`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approverKey, decision, reason: reason || '' }),
    }
  );
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
  return res.json();
}

/** Retry writing an approved change that failed. Safe to repeat — the write is idempotent. */
export async function applyChangeRequest(requestId: string): Promise<void> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/role-approvals/change-requests/${encodeURIComponent(requestId)}/apply`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
}

/** Withdraw a change request (its requester, or an admin). Frees the role for editing again. */
export async function withdrawChangeRequest(requestId: string): Promise<void> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/role-approvals/change-requests/${encodeURIComponent(requestId)}/withdraw`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error((await errorFrom(res)) || `Request failed (${res.status})`);
}
